import { omit, pick } from 'ramda'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { fetchVailHistory, useOrg } from '@/hooks'
import useValidateEndpoint from '@/hooks/useValidate'
import { ConfigDataScreen } from '@/screens/ComponentLibrary/components'
import { useGetCustomComponent } from '@/screens/ComponentLibrary/hooks'
import { camelize } from '@/utils'

import { HomeBaseSettings } from './Settings'

const parseSettings = (
  settings: HomeBaseSettings,
  outroRecipe?: string | null
) => {
  try {
    return {
      HomeBaseMenuOutro: {
        CustomRecipeOutro: outroRecipe,
        //Conditionally adding the HomeBasePersistentButtons, OutroText, ReturnToHomeBaseMenuButtonText based on the value of outroRecipe,
        //These are required by the schema when outroRecipe is null.
        ...(!outroRecipe && {
          ...pick(
            ['OutroText', 'ReturnToHomeBaseMenuButtonText'],
            settings.HomeBaseMenuOutro
          ),
          HomeBasePersistentButtons:
            settings.HomeBaseMenuOutro.HomeBasePersistentButtons.map((btn) => ({
              label: btn.label,
              url: btn.type === 'phone' ? `tel:${btn.value}` : btn.value,
            })),
        }),

        ...omit(
          [
            'OutroText',
            'ReturnToHomeBaseMenuButtonText',
            'HomeBasePersistentButtons',
          ],
          settings.HomeBaseMenuOutro
        ),
      },
      ...pick(
        [
          'interactionsBeforeSentiment',
          'HomeBaseMenuIntroText',
          'HomeBaseMenuText',
          'NumberOfEGsPerMenu',
          'ShowUserMoreItemsText',
          'SkipMenuOnSingleEG',
          'egOrder',
        ],
        settings
      ),
    }
  } catch (e) {
    return {
      error: 'Out of date',
      message: 'Please save the component to update to the latest version',
    }
  }
}
export const parseHomeBaseData = async (
  orgSlug?: string | null,
  param?: { settings: HomeBaseSettings } | string | null
) => {
  let homeBaseData: { settings: HomeBaseSettings; name: string } | null

  try {
    const parsedParam = typeof param === 'string' ? JSON.parse(param) : param

    if (parsedParam?.type?.toLowerCase() === 'homeBase'.toLowerCase())
      homeBaseData = parsedParam
    else homeBaseData = null
  } catch (e) {
    homeBaseData = null
  }

  const outroVersion = homeBaseData?.settings?.customRecipeOutro?.version

  const customRecipeOutro = await fetchVailHistory(outroVersion)
    .then((d) => JSON.parse(d?.data.getVailHistory?.data || '{}'))
    .then((recipe) => recipe?.name)
    .then(
      (recipeName) =>
        `workshop/${orgSlug}/SOLUTION_SELECTED/${camelize(recipeName)}/start`
    )

  if (!homeBaseData || !orgSlug) return {}

  return {
    data: parseSettings(homeBaseData.settings, customRecipeOutro),
    organization: orgSlug,
    schemaName: 'HomeBaseMenuConfig',
    schemaVersion: '1.1.0',
    version: '1.1.0',
  }
}
export const useParseHomeBaseData = (
  orgSlug?: string | null,
  param?: { settings: HomeBaseSettings } | string | null
) => {
  const [customRecipeOutro, setCustomRecipeOutro] = useState<
    string | null | undefined
  >(undefined)

  let homeBaseData: { settings: HomeBaseSettings; name: string } | null

  try {
    const parsedParam = typeof param === 'string' ? JSON.parse(param) : param

    if (parsedParam?.type?.toLowerCase() === 'homeBase'.toLowerCase())
      homeBaseData = parsedParam
    else homeBaseData = null
  } catch (e) {
    homeBaseData = null
  }

  useEffect(() => {
    const outroVersion = homeBaseData?.settings?.customRecipeOutro?.version
    if (!outroVersion) {
      setCustomRecipeOutro(null)
      return
    }

    fetchVailHistory(outroVersion)
      .then((d) => JSON.parse(d?.data.getVailHistory?.data || '{}'))
      .then((recipe) => recipe?.name)
      .then((recipeName) =>
        setCustomRecipeOutro(
          `workshop/${orgSlug}/SOLUTION_SELECTED/${camelize(recipeName)}/start`
        )
      )
      .catch(() => setCustomRecipeOutro(null))
  }, [homeBaseData, orgSlug])

  if (!homeBaseData || !orgSlug) return {}

  return {
    data: parseSettings(homeBaseData.settings, customRecipeOutro),
    organization: orgSlug,
    schemaName: 'HomeBaseMenuConfig',
    schemaVersion: '1.1.0',
    version: '1.1.0',
  }
}
export const ConfigData = () => {
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()
  const org = useOrg(orgID)
  const current = useGetCustomComponent<HomeBaseSettings>(componentID)

  const config = useParseHomeBaseData(org.data?.slug, current.data)
  const valid = useValidateEndpoint(config, componentID)
  return (
    <ConfigDataScreen
      config={config}
      current={current}
      org={org}
      rootName="Home Base Menu"
      valid={valid}
    />
  )
}
